import { useCallback, useEffect, useState } from 'react';
import { debounce, filter } from 'lodash';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { formatStatus } from '@nxsys/utils';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import {
  defaultPaymentFrequency,
  defaultRegistrationStatus,
  paymentModels,
} from '@nxsys/constants/AppConstants';
import { PaginationType } from '@nxsys/types/models/Common';
import { EmployeeRecordType } from '@nxsys/types/models/employee';

type ModalSelectEmployeeProps = {
  isOpen: boolean;
  toggle: () => void;
  companyPaymentId?: string;
  type: 'P6P9' | 'STUDENT_LOAN';
  onConfirm: (employee: EmployeeRecordType) => void;
};

const ModalSelectEmployee: React.FC<ModalSelectEmployeeProps> = ({
  isOpen,
  toggle,
  type,
  onConfirm,
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const [employees, setEmployees] = useState<EmployeeRecordType[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    current: 0,
    pageSize: 20,
    totalDocs: 0,
  });
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeRecordType | null>(null);

  const fetchEmployees = async (params: {
    page: number;
    size: number;
    search?: string;
  }) => {
    setLoading(true);
    try {
      const response = await jwtAxios.get(endPoints.employee.getV2, {
        params: {
          ...params,
          paymentModels: isBureau
            ? ['PAYEE']
            : ['PAYEE', 'UMBRELLA', 'HYBRID', 'PAYE_HYBRID', 'PAYE_FTH'],
        },
      });
      setEmployees(response.data.data || []);
      setPagination({
        current: params.page,
        pageSize: params.size,
        totalDocs: response.data.totalDocs || 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedEmployee(null);
      fetchEmployees({ page: 0, size: 20 });
    }
  }, [isOpen]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      fetchEmployees({ page: 0, size: 20, search: value || undefined });
    }, 500),
    [],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    fetchEmployees({
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<EmployeeRecordType> = [
    {
      key: 'checkbox',
      title: '',
      width: 50,
      fixed: 'left',
      render: (_, record) => (
        <AppCheckbox
          checked={selectedEmployee?.id === record.id}
          onChange={(e) => {
            setSelectedEmployee(e.target.checked ? record : null);
          }}
          size="small"
        />
      ),
    },
    {
      key: 'status',
      title: messages['table.status'],
      dataIndex: 'status',
      align: 'center',
      width: 120,
      render: (status) => (
        <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
          {formatStatus(status)}
        </AppTagStatus>
      ),
    },
    {
      key: 'id',
      title: messages['table.systemId'],
      dataIndex: 'id',
      width: 120,
    },
    {
      key: 'employeeName',
      title: messages['table.employeeName'],
      width: 250,
      render: (record) =>
        record?.fullName ||
        `${record?.forename || ''} ${record?.surname || ''}` ||
        '_',
    },
    {
      key: 'agencyRef',
      title: isBureau
        ? messages['table.employerRef']
        : messages['table.agencyRef'],
      width: 200,
      render: (record) =>
        isBureau
          ? record?.agencyRef || record?.id || '_'
          : `${record?.primaryAgency?.agencyPrefix || '___'}/${
              record?.agencyRef || record?.id || '_'
            }`,
    },
    {
      key: 'companyPayment',
      title: isBureau
        ? messages['table.employer']
        : messages['table.companyPayment'],
      dataIndex: 'companyPayment',
      width: 250,
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'paymentModel',
      title: messages['table.paymentModel'],
      dataIndex: 'setting',
      width: 150,
      render: (value) => {
        const label =
          paymentModels[value?.paymentModel as keyof typeof paymentModels] ||
          'Not Set';
        return isIreland && label === 'CIS' ? 'RCT' : label;
      },
    },
    {
      key: 'registrationStatus',
      title: messages['table.registrationStatus'],
      dataIndex: 'registrationStatus',
      align: 'center',
      width: 160,
      render: (value) =>
        filter(defaultRegistrationStatus, (item) => item.value === value)?.[0]
          ?.label || '',
    },
    {
      key: 'payFrequency',
      title: messages['table.payFrequency'],
      dataIndex: 'payFrequency',
      align: 'center',
      width: 140,
      render: (value) =>
        filter(
          defaultPaymentFrequency,
          (item) => item?.value === value,
        )?.[0]?.label || '',
    },
    {
      key: 'NINo',
      title: getFieldLabelByCountry(payrollCountry ?? '', 'NINo'),
      width: 140,
      render: (record) =>
        isIreland
          ? record?.setting?.ppsNumber || ''
          : record.NINumber || '',
    },
    // Conditional columns based on type
    ...(type === 'P6P9'
      ? ([
          {
            key: 'taxCode',
            title: messages['common.taxCode'],
            width: 120,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.taxCode || '_',
          },
          {
            key: 'taxBasis',
            title: messages['common.taxBasis'],
            width: 120,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.taxBasis || '_',
          },
          {
            key: 'p45GrossToDate',
            title: messages['common.p45GrossToDate'] ?? 'P45 Gross to Date',
            width: 150,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.p45GrossToDate || '_',
          },
          {
            key: 'p45TaxToDate',
            title: messages['common.p45TaxToDate'] ?? 'P45 Tax to Date',
            width: 150,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.p45TaxToDate || '_',
          },
        ] as ColumnsType<EmployeeRecordType>)
      : ([
          {
            key: 'slPlan1Start',
            title: 'SL Plan 1 Start',
            width: 130,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPlan1StartDate ? moment(record.setting.studentLoanPlan1StartDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'slPlan1End',
            title: 'SL Plan 1 End',
            width: 130,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPlan1EndDate ? moment(record.setting.studentLoanPlan1EndDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'slPlan2Start',
            title: 'SL Plan 2 Start',
            width: 130,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPlan2StartDate ? moment(record.setting.studentLoanPlan2StartDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'slPlan2End',
            title: 'SL Plan 2 End',
            width: 130,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPlan2EndDate ? moment(record.setting.studentLoanPlan2EndDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'slPlan4Start',
            title: 'SL Plan 4 Start',
            width: 150,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPlan4StartDate ? moment(record.setting.studentLoanPlan4StartDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'slPlan4End',
            title: 'SL Plan 4 End',
            width: 150,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPlan4EndDate ? moment(record.setting.studentLoanPlan4EndDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'slPlan5Start',
            title: 'SL Plan 5 Start',
            width: 150,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPlan5StartDate ? moment(record.setting.studentLoanPlan5StartDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'slPlan5End',
            title: 'SL Plan 5 End',
            width: 150,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPlan5EndDate ? moment(record.setting.studentLoanPlan5EndDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'postGradStart',
            title: 'PostGrad Start',
            width: 150,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPostGraStartDate ? moment(record.setting.studentLoanPostGraStartDate).format('DD/MM/YYYY') : '_',
          },
          {
            key: 'postGradEnd',
            title: 'PostGrad End',
            width: 150,
            render: (_: unknown, record: EmployeeRecordType) =>
              record.setting?.studentLoanPostGraEndDate ? moment(record.setting.studentLoanPostGraEndDate).format('DD/MM/YYYY') : '_',
          },
        ] as ColumnsType<EmployeeRecordType>)),
  ];

  return (
    <AppModalContainer
      title="Employees"
      width={1200}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          buttonType: 'default',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => {
            if (selectedEmployee) onConfirm(selectedEmployee);
          },
          disabled: !selectedEmployee,
        },
      ]}
      noPaddingBody
    >
      <div className="px-5 py-4">
        <AppInputSearch
          placeholder={messages['common.search']}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            debouncedSearch(e.target.value);
          }}
          className="!w-full md:!w-[280px]"
        />
      </div>
      <AppTableContainer
        rowKey="id"
        data={employees}
        loading={loading}
        columns={columns}
        page={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.totalDocs}
        onChangePagination={onChangePagination}
        scroll={{ x: 2500, y: 400 }}
      />
    </AppModalContainer>
  );
};

export default ModalSelectEmployee;
